/**
 * Utilitário para seleção de avatares 3D
 * 
 * Usado exclusivamente para usuários do sistema e vendedores.
 * Seleciona avatar consistente baseado no hash do identificador (nome/email).
 * 
 * @version 2.0.0
 * - Adicionado 4 novos avatares (total 12)
 * - Adicionado getAll3DAvatars() para seleção manual
 * - Adicionado get3DAvatarById() para buscar por ID
 */

// Importa todos os avatares 3D
import avatar1 from '@/assets/avatars/avatar-1.png';
import avatar2 from '@/assets/avatars/avatar-2.png';
import avatar3 from '@/assets/avatars/avatar-3.png';
import avatar4 from '@/assets/avatars/avatar-4.png';
import avatar5 from '@/assets/avatars/avatar-5.png';
import avatar6 from '@/assets/avatars/avatar-6.png';
import avatar7 from '@/assets/avatars/avatar-7.png';
import avatar8 from '@/assets/avatars/avatar-8.png';
import avatar9 from '@/assets/avatars/avatar-9.png';
import avatar10 from '@/assets/avatars/avatar-10.png';
import avatar11 from '@/assets/avatars/avatar-11.png';
import avatar12 from '@/assets/avatars/avatar-12.png';

// Array de avatares disponíveis (12 opções)
const AVATARS_3D = [
  avatar1, avatar2, avatar3, avatar4,
  avatar5, avatar6, avatar7, avatar8,
  avatar9, avatar10, avatar11, avatar12
];

/**
 * Retorna URL do avatar 3D baseado no identificador
 * Mesmo identificador sempre retorna o mesmo avatar (consistência visual)
 * 
 * @param identifier - Nome, email ou ID do usuário/vendedor
 * @returns URL do avatar 3D selecionado
 */
export function get3DAvatarUrl(identifier: string | null | undefined): string {
  if (!identifier || identifier.trim() === '') {
    return AVATARS_3D[0]; // Fallback para primeiro avatar
  }
  
  // Hash simples para garantir consistência
  // Mesmo identificador sempre retorna o mesmo avatar
  const hash = identifier
    .trim()
    .toLowerCase()
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  return AVATARS_3D[hash % AVATARS_3D.length];
}

/**
 * Retorna o número total de avatares 3D disponíveis
 */
export function getTotal3DAvatars(): number {
  return AVATARS_3D.length;
}

/**
 * Retorna todos os avatares 3D disponíveis para seleção manual
 * Cada item contém ID e URL do avatar
 * 
 * @returns Array de objetos { id: string, url: string }
 */
export function getAll3DAvatars(): { id: string; url: string }[] {
  return AVATARS_3D.map((url, index) => ({
    id: `avatar-${index + 1}`,
    url
  }));
}

/**
 * Retorna a URL de um avatar 3D pelo seu ID
 * 
 * @param avatarId - ID do avatar (ex: "avatar-5")
 * @returns URL do avatar ou null se não encontrado
 */
export function get3DAvatarById(avatarId: string): string | null {
  if (!avatarId || !avatarId.startsWith('avatar-')) {
    return null;
  }
  
  const index = parseInt(avatarId.replace('avatar-', ''), 10) - 1;
  
  if (isNaN(index) || index < 0 || index >= AVATARS_3D.length) {
    return null;
  }
  
  return AVATARS_3D[index];
}

/**
 * Verifica se uma string é um ID de avatar 3D válido
 * 
 * @param value - Valor a verificar
 * @returns true se for um ID de avatar válido
 */
export function is3DAvatarId(value: string | null | undefined): boolean {
  if (!value) return false;
  return value.startsWith('avatar-') && get3DAvatarById(value) !== null;
}
